// ============================================================
// HUD - 迷宫游戏界面
// ============================================================

export class Hud {
  private readonly container = document.createElement('div');
  private readonly timerEl = document.createElement('div');
  private readonly gemEl = document.createElement('div');
  private readonly minimapEl = document.createElement('canvas');
  private readonly startScreen = document.createElement('div');
  private readonly endScreen = document.createElement('div');
  private readonly endTitle = document.createElement('h2');
  private readonly endScore = document.createElement('p');
  private readonly endBtn = document.createElement('button');
  private readonly levelEl = document.createElement('div');

  private minimapCtx: CanvasRenderingContext2D;

  constructor() {
    // 容器
    this.container.id = 'maze-hud';
    this.container.style.cssText =
      'position:fixed;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:10;font-family:sans-serif;';

    // 关卡名
    this.levelEl.style.cssText =
      'position:absolute;top:16px;left:50%;transform:translateX(-50%);color:#aaa;font-size:14px;letter-spacing:2px;text-transform:uppercase;';
    this.container.appendChild(this.levelEl);

    // 倒计时
    this.timerEl.style.cssText =
      'position:absolute;top:40px;left:50%;transform:translateX(-50%);font-size:48px;font-weight:bold;color:#fff;text-shadow:0 0 20px rgba(0,0,0,0.8);font-variant-numeric:tabular-nums;';
    this.container.appendChild(this.timerEl);

    // 宝石计数
    this.gemEl.style.cssText =
      'position:absolute;top:16px;right:24px;font-size:22px;color:#ffcc00;text-shadow:0 0 10px rgba(255,170,0,0.5);';
    this.container.appendChild(this.gemEl);

    // 小地图
    this.minimapEl.style.cssText =
      'position:absolute;top:16px;left:16px;width:140px;height:140px;border:1px solid rgba(255,255,255,0.15);border-radius:4px;background:rgba(0,0,0,0.55);';
    this.container.appendChild(this.minimapEl);
    this.minimapCtx = this.minimapEl.getContext('2d')!;
    this.minimapEl.width = 140;
    this.minimapEl.height = 140;

    // 开始界面
    this.startScreen.id = 'maze-start-screen';
    this.startScreen.style.cssText =
      'position:absolute;top:0;left:0;width:100%;height:100%;display:flex;flex-direction:column;align-items:center;justify-content:center;background:rgba(60,80,100,0.7);pointer-events:auto;';
    this.startScreen.innerHTML = `
      <h1 style="color:#ffaa33;font-size:48px;margin:0 0 8px;text-shadow:0 0 30px rgba(255,170,51,0.5);">3D 迷宫</h1>
      <p style="color:#aaa;font-size:16px;margin:0 0 32px;">找到出口，收集宝石，争分夺秒</p>
      <button id="maze-start-btn" style="padding:14px 48px;font-size:20px;background:#ffaa33;color:#111;border:none;border-radius:6px;cursor:pointer;font-weight:bold;letter-spacing:1px;">开始游戏</button>
      <div style="margin-top:32px;color:#888;font-size:13px;text-align:center;line-height:1.8;">
        <div>WASD / 方向键 - 移动</div>
        <div>鼠标 - 视角控制</div>
        <div>Shift - 冲刺加速</div>
        <div>ESC - 暂停</div>
      </div>
    `;
    this.container.appendChild(this.startScreen);

    // 结束界面
    this.endScreen.style.cssText =
      'position:absolute;top:0;left:0;width:100%;height:100%;display:none;flex-direction:column;align-items:center;justify-content:center;background:rgba(0,0,0,0.8);pointer-events:auto;';
    this.endTitle.style.cssText = 'font-size:42px;margin:0 0 16px;';
    this.endScore.style.cssText = 'color:#ccc;font-size:20px;margin:0 0 24px;';
    this.endBtn.style.cssText =
      'padding:14px 48px;font-size:20px;background:#ffaa33;color:#111;border:none;border-radius:6px;cursor:pointer;font-weight:bold;';
    this.endScreen.appendChild(this.endTitle);
    this.endScreen.appendChild(this.endScore);
    this.endScreen.appendChild(this.endBtn);
    this.container.appendChild(this.endScreen);

    document.body.appendChild(this.container);
  }

  showStartScreen(show: boolean): void {
    this.startScreen.style.display = show ? 'flex' : 'none';
  }

  getStartButton(): HTMLElement {
    return this.startScreen.querySelector('#maze-start-btn')!;
  }

  showEndScreen(won: boolean, score: number, onRetry: () => void, onNext?: () => void): void {
    if (won) {
      this.endTitle.textContent = '通关!';
      this.endTitle.style.color = '#ffaa33';
      this.endScore.textContent = `得分: ${score}  宝石: 全部收集`;
      this.endBtn.textContent = '下一关';
      this.endBtn.onclick = () => {
        this.endScreen.style.display = 'none';
        onNext?.();
      };
    } else {
      this.endTitle.textContent = '时间到!';
      this.endTitle.style.color = '#ff4444';
      this.endScore.textContent = `得分: ${score}`;
      this.endBtn.textContent = '重新开始';
      this.endBtn.onclick = () => {
        this.endScreen.style.display = 'none';
        onRetry();
      };
    }
    this.endScreen.style.display = 'flex';
  }

  hideEndScreen(): void {
    this.endScreen.style.display = 'none';
  }

  updateTimer(timeLeft: number): void {
    const mins = Math.floor(timeLeft / 60);
    const secs = Math.floor(timeLeft % 60);
    this.timerEl.textContent = `${mins}:${secs.toString().padStart(2, '0')}`;

    if (timeLeft <= 10) {
      this.timerEl.style.color = timeLeft % 1 < 0.5 ? '#ff3333' : '#ff6666';
    } else if (timeLeft <= 30) {
      this.timerEl.style.color = '#ffaa33';
    } else {
      this.timerEl.style.color = '#ffffff';
    }
  }

  updateGems(collected: number, total: number): void {
    this.gemEl.textContent = `💎 ${collected}/${total}`;
  }

  updateLevel(name: string): void {
    this.levelEl.textContent = name;
  }

  updateMinimap(
    playerX: number,
    playerZ: number,
    yaw: number,
    mazeWalls: boolean[][][],
    gridSize: number,
    cellSize: number,
    visitedCells: Set<string>,
    exitRow: number,
    exitCol: number,
  ): void {
    const ctx = this.minimapCtx;
    const w = 140;
    const h = 140;
    const scale = w / (gridSize * cellSize);

    ctx.clearRect(0, 0, w, h);

    // 记录访问
    const pRow = Math.floor(playerZ / cellSize);
    const pCol = Math.floor(playerX / cellSize);
    if (pRow >= 0 && pRow < gridSize && pCol >= 0 && pCol < gridSize) {
      visitedCells.add(`${pRow},${pCol}`);
    }

    // 绘制已探索区域
    ctx.fillStyle = 'rgba(30,30,50,0.6)';
    for (const key of visitedCells) {
      const [r, c] = key.split(',').map(Number);
      ctx.fillRect(c * cellSize * scale, r * cellSize * scale, cellSize * scale, cellSize * scale);
    }

    // 绘制墙壁
    ctx.strokeStyle = 'rgba(255,255,255,0.2)';
    ctx.lineWidth = 1;
    for (let row = 0; row < gridSize; row++) {
      for (let col = 0; col < gridSize; col++) {
        const cellKey = `${row},${col}`;
        if (!visitedCells.has(cellKey)) continue;

        const x = col * cellSize * scale;
        const y = row * cellSize * scale;
        const s = cellSize * scale;

        if (mazeWalls[row][col][0]) {
          ctx.beginPath();
          ctx.moveTo(x, y);
          ctx.lineTo(x + s, y);
          ctx.stroke();
        }
        if (mazeWalls[row][col][2]) {
          ctx.beginPath();
          ctx.moveTo(x, y + s);
          ctx.lineTo(x + s, y + s);
          ctx.stroke();
        }
        if (mazeWalls[row][col][3]) {
          ctx.beginPath();
          ctx.moveTo(x, y);
          ctx.lineTo(x, y + s);
          ctx.stroke();
        }
        if (mazeWalls[row][col][1]) {
          ctx.beginPath();
          ctx.moveTo(x + s, y);
          ctx.lineTo(x + s, y + s);
          ctx.stroke();
        }
      }
    }

    // 绘制出口
    if (visitedCells.has(`${exitRow},${exitCol}`)) {
      ctx.fillStyle = '#ffaa33';
      ctx.fillRect(
        exitCol * cellSize * scale + 2,
        exitRow * cellSize * scale + 2,
        cellSize * scale - 4,
        cellSize * scale - 4,
      );
    }

    // 绘制玩家
    const px = playerX * scale;
    const pz = playerZ * scale;
    ctx.save();
    ctx.translate(px, pz);
    ctx.rotate(-yaw);
    ctx.fillStyle = '#44ff44';
    ctx.beginPath();
    ctx.moveTo(6, 0);
    ctx.lineTo(-4, -4);
    ctx.lineTo(-4, 4);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  dispose(): void {
    this.container.remove();
  }
}
